import { faCheck, faCircleExclamation } from '@fortawesome/pro-solid-svg-icons'
import { Icon } from '@/ui'
import { PendingDots, StatusRow } from './StatusRow'
import {
  ToolInvocationState,
  TOOL_STATUS_CONFIG,
  DEFAULT_PENDING,
} from '../lib/constants'
import type { ToolInvocation } from '../lib/types'

interface ToolStatusProps {
  invocation: ToolInvocation
}

interface ParsedToolResult {
  error?: string
  success?: boolean
  empty?: boolean
  alreadyAttached?: boolean
  refused?: boolean
}

const parseResult = (result: unknown): ParsedToolResult => {
  try {
    return typeof result === 'string'
      ? JSON.parse(result)
      : ((result ?? {}) as ParsedToolResult)
  } catch {
    return {}
  }
}

export const ToolStatus = ({ invocation }: ToolStatusProps) => {
  const copy = TOOL_STATUS_CONFIG[invocation.toolName]
  if (!copy) return null

  const threw = invocation.state === ToolInvocationState.OutputError
  const isPending =
    !threw && invocation.state !== ToolInvocationState.OutputAvailable
  const parsed = isPending || threw ? {} : parseResult(invocation.result)

  // No new side effect happened: the BE deduped a re-fired tool, or a two-phase
  // tool refused its confirmation. Render no card.
  if (parsed.alreadyAttached || parsed.refused) return null

  const isError = threw || Boolean(parsed.error)

  let label: string
  if (isPending) {
    label = copy.pending ?? DEFAULT_PENDING
  } else if (isError) {
    label = copy.error
  } else if (parsed.empty) {
    label = copy.emptySuccess ?? copy.success
  } else {
    label = copy.success
  }

  return (
    <StatusRow
      label={label}
      pending={isPending}
      trailing={
        isPending ? (
          <PendingDots />
        ) : (
          <Icon
            icon={isError ? faCircleExclamation : faCheck}
            color={isError ? 'text-orange-100' : 'text-green-100'}
            className="text-sm"
          />
        )
      }
    />
  )
}
